/**
 * Shared HTML safety + normalization helpers for the blog CMS.
 *
 * Blog bodies are stored as an HTML string in `blogs.content` and rendered on the
 * public site with `dangerouslySetInnerHTML` inside a `prose prose-lg max-w-none`
 * wrapper. Everything that writes to (editor save) or reads from (editor load,
 * previews, read-time) that column should go through this module so the editor and
 * the public renderer agree on exactly one definition of "safe blog HTML".
 *
 * Two jobs live here:
 *  1. Safety: strip anything that could execute, phone home, or break out of the
 *     article shell (`sanitizeBlogHtml`).
 *  2. Typography hygiene: pasted content from Word/Docs/other CMSs carries inline
 *     styling that fights the Tailwind `prose` theme. We keep only the handful of
 *     display properties the editor itself can produce and drop the rest.
 */

import DOMPurify from 'dompurify';

/* -------------------------------------------------------------------------- */
/* Allow lists                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Elements a blog body may contain. Anything else is unwrapped by DOMPurify
 * (its text content survives, the tag does not).
 */
export const BLOG_ALLOWED_TAGS: string[] = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  // Clinical and scientific copy relies on these. Without them a pasted
  // "H<sub>2</sub>O" or "5 cm<sup>2</sup>" is silently flattened to "H2O" and
  // "5 cm2", which is real content loss, and they carry no script surface.
  'sub',
  'sup',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'hr',
  'a',
  'img',
  'figure',
  'figcaption',
  'table',
  'thead',
  'tbody',
  // A pasted table keeps its footer, caption and column grouping rather than
  // having those rows quietly unwrapped into the body.
  'tfoot',
  'caption',
  'colgroup',
  'col',
  'tr',
  'th',
  'td',
  'span',
  'div',
];

/**
 * Attributes a blog body may carry. `style` survives DOMPurify but is then
 * reduced property by property by `filterStyleAttribute`.
 */
export const BLOG_ALLOWED_ATTR: string[] = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'title',
  'colspan',
  'rowspan',
  'style',
  'class',
];

/**
 * Tags that must never survive, even if some future edit widens the allow list.
 * Listed explicitly so the intent is readable at the call site.
 */
const BLOG_FORBIDDEN_TAGS: string[] = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'meta',
  'link',
  'base',
];

/**
 * The only CSS properties allowed to reach the published article. Everything else
 * (font-family, font-size, margin, position, background images, and so on) is
 * dropped so the `prose` theme stays in charge of typography.
 */
const ALLOWED_STYLE_PROPS = new Set(['text-align', 'color', 'background-color']);

/**
 * Protocol allow list handed to DOMPurify. Mirrors DOMPurify's own default shape
 * (the trailing alternatives are what permit relative URLs) but narrows the
 * protocol set to http, https, mailto and tel. `href`/`src` values are then
 * re-checked by `isSafeUrl`, which is the stricter of the two gates.
 */
const BLOG_ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/** Absolute URLs and in-document references we accept on `href` / `src`. */
const SAFE_URL_PATTERN = /^(?:https?:\/\/[^/]|mailto:|tel:|#|\/(?!\/))/i;

/**
 * Inline images pasted into the editor arrive as data URIs. Only raster formats are
 * accepted: `image/svg+xml` is deliberately excluded because SVG is an active
 * content format, and an inline SVG payload is a far softer target than a PNG.
 */
const SAFE_DATA_IMAGE_PATTERN = /^data:image\/(?:png|jpe?g|gif|webp|avif|bmp)(?:;[a-z0-9+.=;-]*)?,/i;

/**
 * Code points a browser ignores while parsing a URL. A tab or a newline dropped
 * into the middle of a protocol name still navigates, so they have to come out
 * before the protocol test rather than after it. This is the same set DOMPurify
 * strips internally. Written as a lookup rather than a character class because a
 * control character range in a regex literal trips the no-control-regex lint rule.
 */
const URL_IGNORED_CODE_POINTS = new Set([
  0x00a0, 0x1680, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008,
  0x2009, 0x200a, 0x2028, 0x2029, 0x202f, 0x205f, 0x3000, 0xfeff,
]);

/** Matches an opening tag, closing tag, comment or doctype. */
const HTML_TAG_PATTERN = /<[a-z!/][^>]*>/i;

/* -------------------------------------------------------------------------- */
/* URL + CSS value validation                                                  */
/* -------------------------------------------------------------------------- */

/** Removes every code point a browser would ignore while parsing a URL. */
function stripUrlWhitespace(value: string): string {
  let cleaned = '';
  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (codePoint <= 0x20) continue;
    if (URL_IGNORED_CODE_POINTS.has(codePoint)) continue;
    cleaned += char;
  }
  return cleaned;
}

/**
 * True when a `href` / `src` value is one of: absolute http(s), mailto, tel, an
 * in-page fragment, a root relative path, or a raster data image.
 *
 * Protocol relative URLs (`//example.com`) are rejected on purpose: they inherit
 * the page protocol and read as a path to anyone skimming the markup.
 */
function isSafeUrl(value: string): boolean {
  const candidate = stripUrlWhitespace(value);
  if (!candidate) return false;
  if (SAFE_URL_PATTERN.test(candidate)) return true;
  return SAFE_DATA_IMAGE_PATTERN.test(candidate);
}

/** Constructs that have no business in a blog style attribute. */
const UNSAFE_CSS_TOKEN_PATTERN = /url\(|expression\(|javascript:|@import|[\\<>{}]|\/\*/i;

const TEXT_ALIGN_PATTERN = /^(?:left|right|center|justify|start|end)$/i;

/**
 * Hex, rgb()/rgba(), hsl()/hsla(), or a bare CSS colour keyword. The character
 * classes inside the functional forms are intentionally narrow so nothing that
 * looks like a nested function or a URL can slip through.
 */
const CSS_COLOR_PATTERN =
  /^(?:#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/a-z]+\)|[a-z]+)$/i;

function isSafeStyleValue(prop: string, value: string): boolean {
  if (!value || UNSAFE_CSS_TOKEN_PATTERN.test(value)) return false;
  // `!important` would let pasted markup outrank the prose theme.
  if (value.toLowerCase().includes('!important')) return false;
  if (prop === 'text-align') return TEXT_ALIGN_PATTERN.test(value);
  return CSS_COLOR_PATTERN.test(value);
}

/**
 * Reduces a style attribute to the allowed display properties, dropping every
 * other declaration. Returns an empty string when nothing survives, in which case
 * the caller removes the attribute entirely.
 */
function filterStyleAttribute(style: string): string {
  const kept: string[] = [];
  // None of the allowed property values can contain a semicolon, so a plain split
  // is a correct declaration splitter here.
  for (const declaration of style.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator === -1) continue;
    const prop = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim();
    if (!ALLOWED_STYLE_PROPS.has(prop)) continue;
    if (!isSafeStyleValue(prop, value)) continue;
    kept.push(`${prop}: ${value}`);
  }
  return kept.join('; ');
}

/* -------------------------------------------------------------------------- */
/* DOMPurify instance                                                          */
/* -------------------------------------------------------------------------- */

type Purifier = ReturnType<typeof DOMPurify>;

const PURIFY_CONFIG = {
  ALLOWED_TAGS: BLOG_ALLOWED_TAGS,
  ALLOWED_ATTR: BLOG_ALLOWED_ATTR,
  FORBID_TAGS: BLOG_FORBIDDEN_TAGS,
  ALLOWED_URI_REGEXP: BLOG_ALLOWED_URI_REGEXP,
  // The attribute allow list above is the whole contract; data-* would let pasted
  // markup smuggle in hooks for scripts that live elsewhere on the page.
  ALLOW_DATA_ATTR: false,
  // aria-* stays on: it is inert and it is the only accessibility affordance an
  // author can carry over from a pasted document.
  ALLOW_ARIA_ATTR: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
} as const;

/**
 * Post-pass applied to every surviving element. Runs after DOMPurify has already
 * enforced the tag and attribute allow lists, so this only has to tighten values.
 */
function hardenAttributes(node: Element): void {
  if (node.hasAttribute('style')) {
    const filtered = filterStyleAttribute(node.getAttribute('style') ?? '');
    if (filtered) {
      node.setAttribute('style', filtered);
    } else {
      node.removeAttribute('style');
    }
  }

  for (const attribute of ['href', 'src']) {
    if (!node.hasAttribute(attribute)) continue;
    if (!isSafeUrl(node.getAttribute(attribute) ?? '')) {
      node.removeAttribute(attribute);
    }
  }

  // A new tab handed a window.opener reference can navigate the article away.
  if (node.nodeName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

let purifier: Purifier | null = null;
let purifierUnsupported = false;

/**
 * Lazily builds an isolated DOMPurify instance. Isolated on purpose: hooks in
 * DOMPurify are registered per instance, and the shared default instance is used
 * elsewhere in the app for SVG template markup that legitimately needs the styles
 * this module strips.
 *
 * Returns null when there is no DOM to work with (server rendering, node scripts)
 * or when the runtime cannot support DOMPurify.
 */
function getPurifier(): Purifier | null {
  if (purifier) return purifier;
  if (purifierUnsupported) return null;

  // Deliberately not memoized: a module can be evaluated without a DOM and used
  // again later once one exists, and that second call must get a real purifier.
  if (typeof window === 'undefined' || typeof window.document === 'undefined') {
    return null;
  }

  const instance = DOMPurify();
  if (!instance.isSupported) {
    purifierUnsupported = true;
    return null;
  }

  instance.addHook('afterSanitizeAttributes', (node) => {
    hardenAttributes(node);
  });

  purifier = instance;
  return purifier;
}

/**
 * BEST EFFORT ONLY. NOT A SECURITY BOUNDARY.
 *
 * Used when this module is imported where no DOM exists (server rendering, build
 * scripts, node tooling) so an import cannot crash the process. Regexes cannot
 * parse HTML, so treat any output of this path as untrusted: it exists to keep a
 * server render readable, and the real sanitization is the DOMPurify pass that
 * runs in the browser before the value is ever written back to the database.
 */
function fallbackStrip(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(
      /<\s*(script|style|iframe|object|embed|form|noscript)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi,
      '',
    )
    .replace(/<\s*\/?\s*(script|style|iframe|object|embed|form|input|meta|link|base)\b[^>]*>/gi, '')
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    // `data:image/` is spared so inline images survive a server render.
    .replace(
      /\s(?:href|src)\s*=\s*"(?:\s|&#\w+;)*(?:javascript|vbscript|data(?!:image\/)):[^"]*"/gi,
      '',
    )
    .replace(
      /\s(?:href|src)\s*=\s*'(?:\s|&#\w+;)*(?:javascript|vbscript|data(?!:image\/)):[^']*'/gi,
      '',
    );
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Sanitizes a blog body for storage and for rendering.
 *
 * Run this on every save and before every `dangerouslySetInnerHTML`. It is
 * idempotent, so sanitizing already sanitized content is free of surprises.
 */
export function sanitizeBlogHtml(html: string): string {
  if (!html) return '';

  const instance = getPurifier();
  if (!instance) return fallbackStrip(html);

  return String(instance.sanitize(html, PURIFY_CONFIG));
}

/**
 * Escapes the five characters that change meaning inside HTML markup.
 * `&` has to go first or the other replacements get double escaped.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Converts plain text into paragraph markup: blank lines start a new paragraph,
 * single newlines become line breaks, and the text itself is escaped.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return '';

  const normalized = text.replace(/\r\n?/g, '\n').trim();
  if (!normalized) return '';

  return normalized
    .split(/\n[ \t]*\n+/)
    .map((block) =>
      block
        .split('\n')
        .map((line) => escapeHtml(line.trim()))
        .join('<br />'),
    )
    .filter((block) => block.length > 0 && block !== '<br />')
    .map((block) => `<p>${block}</p>`)
    .join('');
}

/**
 * Prepares a stored blog body for the editor.
 *
 * Older posts were written in a plain textarea, so `blogs.content` can hold raw
 * text with no markup at all. Loading that straight into a rich text editor
 * collapses every line break, so text-only bodies are promoted to paragraphs
 * first. Anything that already contains markup is handed back untouched.
 *
 * There is no legacy class rewriting here on purpose: production content carries
 * no editor specific classes to map.
 */
export function normalizeIncomingHtml(raw: string): string {
  if (!raw) return '';

  const trimmed = raw.trim();
  if (!trimmed) return '';

  if (!HTML_TAG_PATTERN.test(trimmed)) return plainTextToHtml(raw);

  return raw;
}

/** Named entities worth decoding when reducing markup back to readable text. */
const TEXT_ENTITIES: Array<[RegExp, string]> = [
  [/&nbsp;|&#160;|&#xa0;/gi, ' '],
  [/&lt;|&#60;/gi, '<'],
  [/&gt;|&#62;/gi, '>'],
  [/&quot;|&#34;/gi, '"'],
  [/&#39;|&apos;|&#x27;/gi, "'"],
  // Ampersand goes last so `&amp;lt;` decodes to the literal text `&lt;`.
  [/&amp;|&#38;/gi, '&'],
];

/** Tags that end a run of text, so removing them must not glue words together. */
const BLOCK_LEVEL_PATTERN =
  /<\/?(?:p|div|br|li|tr|td|th|h[1-6]|blockquote|pre|figure|figcaption|table|thead|tbody|ul|ol|hr)\b[^>]*>/gi;

/**
 * Reduces markup to readable plain text. Pure regex and DOM free, so it is safe to
 * call during server rendering, in tests, and in node scripts.
 *
 * This is a text extractor, not a sanitizer. Never feed its output back into
 * `dangerouslySetInnerHTML`.
 */
export function stripTags(html: string): string {
  if (!html) return '';

  let text = html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    // Script and style bodies are code, not prose, so drop their contents too.
    .replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, ' ')
    .replace(BLOCK_LEVEL_PATTERN, ' ')
    .replace(/<[^>]*>/g, '');

  for (const [pattern, replacement] of TEXT_ENTITIES) {
    text = text.replace(pattern, replacement);
  }

  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Counts the words in a blog body, tags excluded.
 *
 * Read time used to be derived by splitting the raw HTML on whitespace, which
 * counted every tag and attribute as words and inflated the estimate badly on
 * heavily formatted posts. Stripping first is the fix.
 */
export function htmlWordCount(html: string): number {
  const text = stripTags(html);
  if (!text) return 0;
  return text.split(/\s+/).filter((word) => word.length > 0).length;
}
